import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form, Input, notification, Typography } from 'antd'
import useMutation from 'hooks/useMutation'
import { useRecoilState } from 'recoil'
import {
  revalidateFileListState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import useSWR from 'swr'
import { Point } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

import { OpenModalDelete } from '../../../../types/building'

type Props = {
  open: OpenModalDelete | undefined
  setOpen: Dispatch<SetStateAction<OpenModalDelete>>
  currentPointInfo: Point
}

export default function ModalChangeImageName({
  open,
  setOpen,
  currentPointInfo
}: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const [selectedPointDetailState] = useRecoilState(selectedPointDetail)
  const [currentSelectedPoint] = useRecoilState(selectedPoint)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)
  const [, setPointDetail] = useRecoilState(selectedPointDetail)

  const [form] = Form.useForm()

  const { mutate: refetchPoints } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId: id,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const { trigger: handleUpdateName } = useMutation(
    `/v1/points/${currentSelectedPoint}/file-name`
  )

  const handleClose = () => {
    setOpen({ isOpen: '', pointId: '', pointOrder: 0 })
  }

  const handleChangeName = () => {
    form
      .validateFields()
      .then(() => {
        handleUpdateName(
          {
            method: 'patch',
            data: {
              name: form.getFieldValue('newName').trim(),
              fileId: selectedPointDetailState?.image360?.id
            }
          },
          {
            onSuccess: () => {
              refetchPoints()
              notification.success({
                message: t('building.successChangeStatus'),
                key: t('building.successChangeStatus'),
                duration: 3
              })
              handleClose()
              setRevalidateFileList(1)
              setPointDetail((prev) => {
                if (!prev) return prev
                return {
                  ...prev,
                  image360: {
                    ...prev.image360!,
                    name: form.getFieldValue('newName').trim()
                  }
                }
              })
            }
          }
        )
      })
      .catch(() => {})
  }

  return (
    <Modal
      open={open?.isOpen === 'handleChangeName'}
      onCancel={handleClose}
      width={480}
      isEnterable
      onEnter={handleChangeName}
    >
      <Title title={t('building.titleChangeImageName')} bottomMargin="mb-8" />

      <div className="flex flex-col gap-10 text-center change-image-name-form">
        <Typography className="text-[14px] leading-[21px]">
          {t('building.changeImageNameConfirmation')}
        </Typography>
        <Form
          form={form}
          initialValues={{
            newName: currentPointInfo?.image360?.name
          }}
        >
          <Form.Item
            // validateStatus={isValid ? 'success' : 'error'}
            name="newName"
            rules={[
              {
                required: true,
                message: t('building.nameImageOver')
              },
              {
                min: 1,
                max: 200,
                message: t('building.nameImageOver')
              },
              {
                validator: (_, value) => {
                  const oldName = currentPointInfo?.image360?.name?.trim() || ''
                  const newName = value?.trim() || ''

                  if (newName === oldName) {
                    return Promise.reject(
                      new Error(t('common.changeImageNameModal.sameNameError'))
                    )
                  }
                  return Promise.resolve()
                }
              }
            ]}
          >
            <Input
              className="h-[38px] text-sm"
              placeholder="新しい名前を入力してください"
            />
          </Form.Item>
        </Form>

        <div className="flex gap-10 justify-center">
          <Button sizing="w-[140px] h-[50px]" onClick={handleClose}>
            {t('building.buttonCancel')}
          </Button>

          <Button
            sizing="w-[140px] h-[50px]"
            type="primary"
            onClick={handleChangeName}
          >
            {t('common.changeImageName')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
